import emailjs, { type EmailJSResponseStatus } from "@emailjs/browser";
import type { EmailMessage } from "@/models/EmailMessage";

type MaybePromise<T> = T | Promise<T>;

export type SendEmailOptions = {
  isProd: boolean;

  // allow sync or async dev sender
  sendMailDev: (msg: EmailMessage) => MaybePromise<void>;

  // allow sync or async logging
  logMail?: (msg: EmailMessage, status: string) => MaybePromise<void>;

  onSuccess?: () => void;
  onError?: () => void;

  beforeSend?: () => void;
  afterSuccess?: () => void;
  afterError?: () => void;

  emailJs: {
    serviceId: string;
    templateId: string;
    publicKey: string;
  };

  // EmailJS params
  buildTemplateParams: () => Record<string, any>;

  // IMPORTANT: must return EmailMessage with `to: string`
  buildMessageToLog: () => EmailMessage;

  // Optional guard (checks empty/whitespace)
  requireToEmail?: boolean;
};

export function useEmailNotifications() {
  const send = async (opts: SendEmailOptions) => {
    opts.beforeSend?.();

    const msg = opts.buildMessageToLog();

    if (opts.requireToEmail && !msg.to?.trim()) {
      if (opts.logMail) await opts.logMail(msg, "400 - missing to email");
      opts.onError?.();
      opts.afterError?.();
      return;
    }

    if (!opts.isProd) {
      try {
        await opts.sendMailDev(msg);
        if (opts.logMail) await opts.logMail(msg, "200 - dev");
        opts.onSuccess?.();
        opts.afterSuccess?.();
      } catch (e: any) {
        if (opts.logMail) await opts.logMail(msg, `500 - dev - ${e?.message ?? "unknown error"}`);
        opts.onError?.();
        opts.afterError?.();
      }
      return;
    }

    try {
      const response = await emailjs.send(
        opts.emailJs.serviceId,
        opts.emailJs.templateId,
        opts.buildTemplateParams(),
        opts.emailJs.publicKey
      );

      if (response.status !== 426) {
        if (opts.logMail) await opts.logMail(msg, `${response.status}`);
        opts.onSuccess?.();
        opts.afterSuccess?.();
      } else {
        if (opts.logMail) await opts.logMail(msg, `${response.status} - EmailJS throttle`);
        opts.onError?.();
        opts.afterError?.();
      }
    } catch (error: any) {
      const text = (error as EmailJSResponseStatus)?.text ?? error?.message ?? "unknown error";
      if (opts.logMail) await opts.logMail(msg, `500 - ${text}`);
      opts.onError?.();
      opts.afterError?.();
    }
  };

  return { send };
}
